/**
 * ProgressRing — SVG radial progress indicator (§4 deliverables snapshot + header).
 * Used on Overview (snapshot, size=88) and Deliverables (header, size=104).
 *
 * Two-role amber discipline: the arc is a fill (var(--c-gold)), the center
 * text is body color (var(--c-text)) — no amber text on amber surface.
 *
 * Props:
 *   delivered   {number} — completed item count
 *   total       {number} — total item count
 *   size        {number} — outer SVG diameter in px (default 88)
 *   strokeWidth {number} — ring stroke width in px (default 7)
 */
export function ProgressRing({ delivered, total, size = 88, strokeWidth = 7 }) {
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(delivered / total, 1) : 0;
  const offset = circ * (1 - pct);
  const cx = size / 2;
  const cy = size / 2;
  // Fraction text: "5/8" — sized proportionally so it fits inside any ring
  const fontSize = Math.round(size * 0.18);

  return (
    <svg
      width={size}
      height={size}
      role="img"
      aria-label={`${delivered} of ${total} deliverables complete`}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* Track ring */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="var(--c-surface-3)"
        strokeWidth={strokeWidth}
      />
      {/* Progress arc — starts at top (rotated -90°) */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="var(--c-gold)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        style={{
          transform: `rotate(-90deg)`,
          transformOrigin: `${cx}px ${cy}px`,
          transition: 'stroke-dashoffset 700ms cubic-bezier(0.4,0,0.2,1)',
        }}
      />
      {/* Center label: "5/8" */}
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fill="var(--c-text)"
        fontFamily="var(--font-display)"
        fontWeight="700"
        fontSize={fontSize}
        letterSpacing="0.02em"
      >
        {delivered}/{total}
      </text>
    </svg>
  );
}

export default ProgressRing;
